import { apiGet, apiPost, apiPut, apiDelete } from '../../api/axiosInstance';
import type { AccountDeletionPreview } from '../../../generated/api/models/accountDeletionPreview';
import type { DeletionImpact } from '../../../generated/api/models/deletionImpact';
import type { UserCompanyGrantCreate } from '../../../generated/api/models/userCompanyGrantCreate';
import type { UserCompanyRoleUpdate } from '../../../generated/api/models/userCompanyRoleUpdate';
import {
  User,
  UserCreate,
  UserUpdate,
  UserActivitySummary,
  Company,
  CompanyCreate,
  CompanyUpdate,
  CompanyRole,
  UserCompanyGrant,
  Group,
  GroupCreate,
  GroupUpdate,
  PaginatedResponse,
  HardDeleteRequest,
} from '../types';

export type { AccountDeletionPreview };

const API_BASE = '/api/v1';

// ============== Users ==============

export interface ListUsersParams {
  company_id?: string;
  status?: string;
  role?: string;
  search?: string;
  user_type?: string;
  exclude_user_type?: string;
  include_deleted?: boolean;
  limit?: number;
  offset?: number;
}

export const userApi = {
  list: async (params?: ListUsersParams): Promise<PaginatedResponse<User>> => {
    const searchParams = new URLSearchParams();
    if (params?.company_id) searchParams.set('company_id', params.company_id);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.role) searchParams.set('role', params.role);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.user_type) searchParams.set('user_type', params.user_type);
    if (params?.exclude_user_type) searchParams.set('exclude_user_type', params.exclude_user_type);
    if (params?.include_deleted) searchParams.set('include_deleted', 'true');
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE}/users${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<PaginatedResponse<User>>(url);
  },

  get: async (id: string, includeGroups = false): Promise<{ data: User }> => {
    const url = `${API_BASE}/users/${id}${includeGroups ? '?include_groups=true' : ''}`;
    return apiGet<{ data: User }>(url);
  },

  create: async (data: UserCreate): Promise<{ data: User }> => {
    return apiPost<{ data: User }>(`${API_BASE}/users`, data);
  },

  update: async (id: string, data: UserUpdate): Promise<{ data: User }> => {
    return apiPut<{ data: User }>(`${API_BASE}/users/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    return apiDelete(`${API_BASE}/users/${id}`);
  },

  setGroups: async (id: string, groupIds: string[]): Promise<{ data: User }> => {
    return apiPut<{ data: User }>(`${API_BASE}/users/${id}/groups`, { group_ids: groupIds });
  },

  verify: async (id: string): Promise<{ data: User }> => {
    return apiPost<{ data: User }>(`${API_BASE}/users/${id}/verify`, {});
  },

  setPassword: async (id: string, password: string): Promise<{ data: { success: boolean; message: string } }> => {
    return apiPost<{ data: { success: boolean; message: string } }>(`${API_BASE}/users/${id}/set-password`, { password });
  },

  activate: async (id: string): Promise<{ data: User }> => {
    return apiPost<{ data: User }>(`${API_BASE}/users/${id}/activate`, {});
  },

  deactivate: async (id: string): Promise<{ data: User }> => {
    return apiPost<{ data: User }>(`${API_BASE}/users/${id}/deactivate`, {});
  },

  restore: async (id: string): Promise<{ data: User }> => {
    return apiPost<{ data: User }>(`${API_BASE}/users/${id}/restore`, {});
  },

  getDeletionImpact: async (id: string): Promise<DeletionImpact> => {
    // Endpoint returns the shared DeletionImpact model directly (no {data} wrapper).
    return apiGet<DeletionImpact>(`${API_BASE}/users/${id}/deletion-impact`);
  },

  hardDelete: async (id: string, data: HardDeleteRequest): Promise<{ data: Record<string, unknown> }> => {
    return apiPost<{ data: Record<string, unknown> }>(`${API_BASE}/users/${id}/hard-delete`, data);
  },

  setDeletionReassignTarget: async (id: string, reassignToUserId: string | null): Promise<{ data: User }> => {
    return apiPut<{ data: User }>(`${API_BASE}/users/${id}/deletion-reassign-target`, { reassign_to_user_id: reassignToUserId });
  },

  bulkDelete: async (ids: string[]): Promise<{ deleted: number }> => {
    return apiPost<{ deleted: number }>(`${API_BASE}/users/bulk-delete`, { ids });
  },

  bulkActivate: async (ids: string[]): Promise<{ activated: number }> => {
    return apiPost<{ activated: number }>(`${API_BASE}/users/bulk-activate`, { ids });
  },

  bulkDeactivate: async (ids: string[]): Promise<{ deactivated: number }> => {
    return apiPost<{ deactivated: number }>(`${API_BASE}/users/bulk-deactivate`, { ids });
  },

  getActivitySummary: async (id: string): Promise<{ data: UserActivitySummary }> => {
    return apiGet<{ data: UserActivitySummary }>(`${API_BASE}/users/${id}/activity-summary`);
  },

  // ===== Self-service account deletion =====

  getOwnDeletionPreview: async (): Promise<{ data: AccountDeletionPreview }> => {
    return apiGet<{ data: AccountDeletionPreview }>(`${API_BASE}/users/me/deletion-preview`);
  },

  deleteOwnAccount: async (
    body: { password?: string; confirm_email?: string },
  ): Promise<{ data: { scheduled_purge_at: string; grace_period_days: number } }> => {
    return apiPost<{ data: { scheduled_purge_at: string; grace_period_days: number } }>(
      `${API_BASE}/users/me/delete-account`,
      body,
    );
  },

  cancelAccountDeletion: async (
    token: string,
  ): Promise<{ data: { status: 'cancelled' | 'already_cancelled'; user_id: string } }> => {
    return apiPost<{ data: { status: 'cancelled' | 'already_cancelled'; user_id: string } }>(
      `${API_BASE}/auth/cancel-account-deletion`,
      { token },
    );
  },

  checkHandleAvailable: async (
    handle: string,
    excludeUserId?: string,
  ): Promise<HandleAvailabilityResult> => {
    const params = new URLSearchParams({ handle });
    if (excludeUserId) params.set('user_id', excludeUserId);
    return apiGet<HandleAvailabilityResult>(`${API_BASE}/users/handle-available?${params}`);
  },
};

export interface HandleAvailabilityResult {
  available: boolean;
  reason: 'taken' | 'invalid' | 'reserved' | null;
  message: string | null;
  suggestions: string[];
}

// ============== Companies ==============

export interface ListCompaniesParams {
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export const companyApi = {
  list: async (params?: ListCompaniesParams): Promise<PaginatedResponse<Company>> => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE}/companies${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<PaginatedResponse<Company>>(url);
  },

  get: async (id: string): Promise<{ data: Company }> => {
    return apiGet<{ data: Company }>(`${API_BASE}/companies/${id}`);
  },

  create: async (data: CompanyCreate): Promise<{ data: Company }> => {
    return apiPost<{ data: Company }>(`${API_BASE}/companies`, data);
  },

  update: async (id: string, data: CompanyUpdate): Promise<{ data: Company }> => {
    return apiPut<{ data: Company }>(`${API_BASE}/companies/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    return apiDelete(`${API_BASE}/companies/${id}`);
  },

  /** Companies the caller has access to (via user_company_roles). */
  listMine: async (): Promise<{ data: Company[] }> => {
    return apiGet<{ data: Company[] }>(`${API_BASE}/users/me/companies`);
  },
};

// ============== Per-user company access (admin, multi-company tenants) ==============

export const userCompanyApi = {
  /** List a user's per-company access grants (admin). */
  list: async (userId: string): Promise<{ data: UserCompanyGrant[] }> => {
    return apiGet<{ data: UserCompanyGrant[] }>(`${API_BASE}/users/${userId}/companies`);
  },

  /** Grant a user access to a company with an optional role (defaults to member). */
  add: async (
    userId: string,
    payload: UserCompanyGrantCreate,
  ): Promise<{ data: UserCompanyGrant }> => {
    return apiPost<{ data: UserCompanyGrant }>(`${API_BASE}/users/${userId}/companies`, payload);
  },

  /** Change a user's role in a company. */
  updateRole: async (
    userId: string,
    companyId: string,
    role: CompanyRole,
  ): Promise<{ data: UserCompanyGrant }> => {
    const body: UserCompanyRoleUpdate = { role };
    return apiPut<{ data: UserCompanyGrant }>(
      `${API_BASE}/users/${userId}/companies/${companyId}`,
      body,
    );
  },

  /** Revoke a user's access to a company. */
  remove: async (userId: string, companyId: string): Promise<void> => {
    return apiDelete(`${API_BASE}/users/${userId}/companies/${companyId}`);
  },
};

// ============== Groups ==============

export interface ListGroupsParams {
  include_system?: boolean;
  type?: 'department' | 'office' | 'team' | 'other';
  company_id?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface ListGroupMembersParams {
  limit?: number;
  offset?: number;
}

export const groupApi = {
  list: async (params?: ListGroupsParams): Promise<PaginatedResponse<Group>> => {
    const searchParams = new URLSearchParams();
    if (params?.include_system !== undefined)
      searchParams.set('include_system', String(params.include_system));
    if (params?.type) searchParams.set('type', params.type);
    if (params?.company_id) searchParams.set('company_id', params.company_id);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE}/groups${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<PaginatedResponse<Group>>(url);
  },

  get: async (id: string): Promise<{ data: Group }> => {
    return apiGet<{ data: Group }>(`${API_BASE}/groups/${id}`);
  },

  create: async (data: GroupCreate): Promise<{ data: Group }> => {
    return apiPost<{ data: Group }>(`${API_BASE}/groups`, data);
  },

  update: async (id: string, data: GroupUpdate): Promise<{ data: Group }> => {
    return apiPut<{ data: Group }>(`${API_BASE}/groups/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    return apiDelete(`${API_BASE}/groups/${id}`);
  },

  getMembers: async (id: string, params?: ListGroupMembersParams): Promise<PaginatedResponse<User>> => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE}/groups/${id}/members${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet<PaginatedResponse<User>>(url);
  },

  addMember: async (groupId: string, userId: string): Promise<{ data: User }> => {
    return apiPost<{ data: User }>(`${API_BASE}/groups/${groupId}/members/${userId}`, {});
  },

  removeMember: async (groupId: string, userId: string): Promise<void> => {
    return apiDelete(`${API_BASE}/groups/${groupId}/members/${userId}`);
  },
};
